import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import { isNullish, isEmptyOrBlank, agentTimezoneMapping } from "utils/utils";
import { queryAgentDetail, updateStatus, getRoleList } from "./model";
import { emailFormatValidator, isAllInputValid } from "utils/formValidation";
import s from "./AgentsDetail.module.scss";

import Spin from "components/Spin/Spin";
import Selection from "components/Input/Selection/Selection";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import responseCodeMapping from "utils/response_code_mapping.json";

import EnabledOptions from "utils/commonOptions/EnabledOptions";
import AgentTimezoneOptions from "utils/AgentStatusOptions/AgentTimezoneOptions";
import AgentRoleOptions from "utils/AgentStatusOptions/AgentRoleOptions";
import AgentPermissionOptions from "utils/AgentStatusOptions/AgentPermissionOptions";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";

const AGENTDETAIL_FIELD_INIT_STATE = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  isOTPenable: "",
  timezone: "",
  permission: [],
  status: "",
  roleId: "",
};

const ERROR_INIT_STATE = {
  firstName: false,
  lastName: false,
  email: false,
  role: false,
  isOTPenable: false,
  timezone: false,
  permission: false,
  status: false,
  roleId: false,
};

const AgentsDetailInfo = ({ IsoAgentId, updateCallback }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "AgentMerchantsListGrid";

  const [detailLoading, setDetailLoading] = useState(true);

  const [roleList, setRoleList] = useState([]);
  const [agentDetailFieldInput, setAgentDetailFieldInput] = useState(
    AGENTDETAIL_FIELD_INIT_STATE,
  );
  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);

  const [submitting, setSubmitting] = useState(false);

  const [dataValid, setDataValid] = useState(false);

  useLayoutEffect(() => {
    if (!IsoAgentId) {
      //message "unknown payment id" will be shown
      setLoading(false);
      return;
    }
    initialFetchRolePermissionList();
  }, [IsoAgentId]);

  useLayoutEffect(() => {
    if (!!roleList?.length) {
      initialFetchDetails();
    }
  }, [roleList]);

  const initialFetchRolePermissionList = async () => {
    setDetailLoading(true);
    const payload = {
      page: 0,
      size: 40,
    };
    const apiResponse = await getRoleList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const list = responseBody.data.roleList.map((role) => {
              return {
                key: role.id,
                title: role.name,
                value: role.id,
              };
            });
            setRoleList(list);
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
            setDetailLoading(false);
          }
        }
      }
    }
  };

  const initialFetchDetails = async (getIsOudated) => {
    setDetailLoading(true);
    const apiResponse = await queryAgentDetail(IsoAgentId);
    if (getIsOudated && getIsOudated()) return;
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const resultData = responseBody.data;
            if (resultData) {
              setAgentDetailFieldInput((state) => ({
                ...state,
                isoAgentId: IsoAgentId,
                firstName: resultData.firstName,
                lastName: resultData.lastName,
                email: resultData.email,
                role: resultData.role,
                isOTPenable: resultData.isOTPenable,
                timezone: agentTimezoneMapping(resultData.timezone), // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.
                permission:
                  JSON.parse(resultData.permission ?? "{}")?.allowedComponent ??
                  [],
                status: resultData.status,
                roleId: resultData.roleId,
              }));
              setDataValid(true);
            } else {
              snackbar(
                t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
                "error",
              );
            }
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "fetchFail"), "error");
          }
        }
      }
    }

    setDetailLoading(false);
  };

  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setAgentDetailFieldInput({ ...agentDetailFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      if (agentDetailFieldInput[field]) {
        const isValidValue = callback(agentDetailFieldInput[field]);
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      } else {
        const isValidValue = callback("");
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      }
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt == "") return false;
    return true;
  };

  // ===================== detail field function end ===================== //
  // ===================== detail function  ===================== //

  const sendDetailUpdate = async () => {
    const requiredField = ["firstName", "lastName", "email", "roleId"];
    let hasError = false;
    requiredField.forEach((field) => {
      const fieldValue = agentDetailFieldInput[field];
      if (isEmptyOrBlank(fieldValue)) {
        console.log("error Field: ", field, "fieldValue", fieldValue);
        setError((error) => ({ ...error, [field]: true }));
        hasError = true;
      }
    });

    const allValid = isAllInputValid(
      error.firstName,
      error.lastName,
      error.email,
      error.role,
      error.isOTPenable,
      error.timezone,
      error.permission,
      error.roleId,
      hasError,
    );

    if (!allValid) {
      return;
    }
    setSubmitting(true);
    const payload = { ...agentDetailFieldInput };
    payload.isOTPenable = agentDetailFieldInput.isOTPenable != "0"; // cast isOTPenable to boolean
    payload.timezone = agentTimezoneMapping(payload.timezone); // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.
    payload.permission = JSON.stringify({
      allowedComponent: agentDetailFieldInput.permission,
    });
    const apiResponse = await updateStatus(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(PAGE_LOCALE_KEY + "update.msgFailed") +
                responseCodeMapping[responseBody.responseCode]["en"],
              "error",
            );
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "update.msgSuccess"), "success");
            updateCallback();
          }
        }
      }
    }
    setSubmitting(false);
  };

  const AgentDetailsMainContent = () => {
    return (
      <div className={s.detailItemContainer}>
        <div className={s.content2}>
          <Grid gridId={gridId}>
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.firstName.label")}
              value={agentDetailFieldInput.firstName}
              onchangeFn={(e) => textInputOnChange(e, "firstName")}
              onfocusFn={() => textInputOnFocus("firstName")}
              onblurFn={() => textInputOnBlur("firstName", isNotEmpty)}
              sxParams={{
                textFocus: focus.firstName,
                textError: error.firstName,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.firstName.errorInput",
              )}
            />
            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.lastName.label")}
              value={agentDetailFieldInput.lastName}
              onchangeFn={(e) => textInputOnChange(e, "lastName")}
              onfocusFn={() => textInputOnFocus("lastName")}
              onblurFn={() => textInputOnBlur("lastName", isNotEmpty)}
              sxParams={{
                textFocus: focus.lastName,
                textError: error.lastName,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.lastName.errorInput",
              )}
            />

            <OutlinedText
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.email.label")}
              value={agentDetailFieldInput.email}
              onchangeFn={(e) => textInputOnChange(e, "email")}
              onfocusFn={() => textInputOnFocus("email")}
              onblurFn={() => textInputOnBlur("email", emailFormatValidator)}
              sxParams={{ textFocus: focus.email, textError: error.email }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.email.errorInput2",
              )}
            />
            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.role.label")}
              selectionValue={agentDetailFieldInput.role}
              onchangeFn={(e) => textInputOnChange(e, "role")}
              onfocusFn={() => textInputOnFocus("role")}
              onblurFn={() => textInputOnBlur("role")}
              options={AgentRoleOptions("all")}
              sxParams={{ textFocus: focus.role, textError: error.role }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.role.errorInput",
              )}
            />

            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.isOTPenable.label")}
              selectionValue={agentDetailFieldInput.isOTPenable.toString()}
              onchangeFn={(e) => textInputOnChange(e, "isOTPenable")}
              onfocusFn={() => textInputOnFocus("isOTPenable")}
              onblurFn={() => textInputOnBlur("isOTPenable")}
              options={EnabledOptions("all")}
              sxParams={{
                textFocus: focus.isOTPenable,
                textError: error.isOTPenable,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.isOTPenable.errorInput",
              )}
            />
            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.timezone.label")}
              selectionValue={agentDetailFieldInput.timezone}
              onchangeFn={(e) => textInputOnChange(e, "timezone")}
              onfocusFn={() => textInputOnFocus("timezone")}
              onblurFn={() => textInputOnBlur("timezone")}
              options={AgentTimezoneOptions("all")}
              sxParams={{
                textFocus: focus.timezone,
                textError: error.timezone,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.timezone.errorInput",
              )}
            />
            <Selection
              mandatory={true}
              multiple
              label={t(PAGE_LOCALE_KEY + "detailColumns.permission.label")}
              selectionValue={agentDetailFieldInput.permission}
              onchangeFn={(e) => textInputOnChange(e, "permission")}
              onfocusFn={() => textInputOnFocus("permission")}
              onblurFn={() => textInputOnBlur("permission")}
              options={AgentPermissionOptions("all")}
              sxParams={{
                textFocus: focus.permission,
                textError: error.permission,
              }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.permission.errorInput",
              )}
            />

            <Selection
              mandatory={true}
              label={t(PAGE_LOCALE_KEY + "detailColumns.rolePermission.label")}
              selectionValue={agentDetailFieldInput.roleId}
              onchangeFn={(e) => textInputOnChange(e, "roleId")}
              onfocusFn={() => textInputOnFocus("roleId")}
              onblurFn={() => textInputOnBlur("roleId")}
              options={roleList}
              sxParams={{ textFocus: focus.roleId, textError: error.roleId }}
              errorMessage={t(
                PAGE_LOCALE_KEY + "detailColumns.rolePermission.errorInput",
              )}
            />
          </Grid>
        </div>

        <div className={s.dialogActionContainer}>
          <div></div>
          <div className={s.dialogButtonContainer}>
            <button
              className={[s.okBtn, s.functionalBtn].join(" ")}
              disabled={submitting || detailLoading}
              onClick={() => {
                sendDetailUpdate();
              }}
            >
              {t(PAGE_LOCALE_KEY + "btnUpdate")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s.container}>
      {!detailLoading && !dataValid && (
        <div className={s.contentContainer}>
          <span>{t(PAGE_LOCALE_KEY + "fetchFail")}</span>
        </div>
      )}
      <div className={s.contentContainer}>
        {detailLoading ? <Spin /> : <></>}
        {submitting && <Spin type={"search"} />}
        <div
          className={[
            s.detailsContainer,
            detailLoading ? s.loadingNow : "",
            !dataValid ? s.inactiveTab : "",
          ].join(" ")}
        >
          <div className={s.agentDetails}>{AgentDetailsMainContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AgentsDetailInfo);
